import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { measurementsAPI } from '../services/api';
import { useAlert } from '../context/AlertContext';
import { FiPlus, FiEdit, FiTrash2, FiMaximize2 } from 'react-icons/fi';
import { format } from 'date-fns';

const Measurements = () => {
  const [measurements, setMeasurements] = useState([]);
  const [loading, setLoading] = useState(true);
  const alert = useAlert();

  const fetchMeasurements = useCallback(async () => {
    try {
      setLoading(true);
      const response = await measurementsAPI.getAll();
      setMeasurements(response.data);
    } catch (error) {
      console.error('Error fetching measurements:', error);
      alert.push({ type: 'error', title: 'Load failed', message: 'Could not load measurements.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMeasurements();
  }, [fetchMeasurements]);

  const handleDelete = async (id, garmentType) => {
    if (window.confirm(`Are you sure you want to delete this ${garmentType} measurement?`)) {
      try {
        await measurementsAPI.delete(id);
        fetchMeasurements();
        alert.push({ type: 'success', title: 'Measurement deleted' });
      } catch (error) {
        console.error('Error deleting measurement:', error);
        alert.push({ type: 'error', title: 'Delete failed', message: 'Please try again.' });
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Measurements</h1>
          <p className="text-gray-600 mt-2">Manage customer measurements</p>
        </div>
        <Link
          to="/measurements/new"
          className="mt-4 sm:mt-0 bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition-colors flex items-center space-x-2"
        >
          <FiPlus size={20} />
          <span>Add Measurement</span>
        </Link>
      </div>

      {measurements.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <FiMaximize2 className="mx-auto text-gray-400" size={48} />
          <p className="text-gray-600 mt-4">No measurements found</p>
          <Link
            to="/measurements/new"
            className="inline-block mt-4 text-primary-600 hover:text-primary-700 font-medium"
          >
            Add your first measurement
          </Link>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Garment Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Key Measurements
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {measurements.map((measurement) => {
                  const keyMeasurements = [];
                  if (measurement.chest) keyMeasurements.push(`Chest: ${measurement.chest}"`);
                  if (measurement.waist) keyMeasurements.push(`Waist: ${measurement.waist}"`);
                  if (measurement.shirt_length) keyMeasurements.push(`Length: ${measurement.shirt_length}"`);
                  if (measurement.pant_length) keyMeasurements.push(`Pant: ${measurement.pant_length}"`);

                  return (
                    <tr key={measurement.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Link
                          to={`/customers/${measurement.customer_id}`}
                          className="text-primary-600 hover:text-primary-700 font-medium"
                        >
                          {measurement.customer_name || `Customer #${measurement.customer_id}`}
                        </Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-900 font-medium">
                        {measurement.garment_type}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                        {format(new Date(measurement.measurement_date), 'MMM dd, yyyy')}
                      </td>
                      <td className="px-6 py-4 text-gray-600 text-sm">
                        {keyMeasurements.length > 0 ? keyMeasurements.join(', ') : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <Link
                            to={`/measurements/${measurement.id}/edit`}
                            className="text-primary-600 hover:text-primary-900"
                          >
                            <FiEdit size={18} />
                          </Link>
                          <button
                            onClick={() => handleDelete(measurement.id, measurement.garment_type)}
                            className="text-red-600 hover:text-red-900"
                          >
                            <FiTrash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Measurements;

